// frontend/src/services/api.js
import axios from "axios";
import { API_CONFIG, AUTH_CONFIG, ENDPOINTS } from "../config";
import { getErrorMessage, logError } from "../utils/errorUtils";

/**
 * Centralized API service for handling all API requests
 * Provides consistent error handling, authentication, and request formatting
 */

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshRequest = null;

/**
 * CORP-FAMILY-005 — Manager-PIN 403 contract predicate.
 *
 * Returns `true` when `error` is an axios error whose response status is 403
 * AND whose response body carries a `code` (or `error`) field that begins
 * with `MANAGER_PIN_` (today: `MANAGER_PIN_INVALID`, `MANAGER_PIN_NOT_PROVISIONED`).
 *
 * Backend contract source-of-truth: `managerPinMiddleware` (PR #205, commit
 * `c095c09`, merged 2026-06-07). PR #205 promoted manager-PIN failures from
 * HTTP 401 to HTTP 403 because the staff session IS authenticated — the
 * caller simply lacks (or fat-fingered) the manager-PIN credential for this
 * specific gated action. 401 incorrectly tripped the FE auth interceptor's
 * "session expired → bounce to /login" branch (the Sprint 13 CFG-1 smoke
 * regression Roderick surfaced).
 *
 * This predicate exists for two reasons:
 *   1. The existing interceptor (below) only branches on `status === 401`, so
 *      403s — including PIN failures — already fall through to
 *      `Promise.reject(error)` and reach the call-site error handler. The
 *      predicate makes that contract EXPLICIT and TESTABLE rather than
 *      coincidental, guarding against a future maintainer adding a 403
 *      branch that would silently break PIN UX.
 *   2. Call-site error handlers can use it to disambiguate "the user's PIN
 *      attempt failed — show inline modal copy" from "some other 403 — show
 *      a global toast". (Today, CorporateProgramsPage.handleUnlinkFamilyMember
 *      keys on `responseData.code` directly; the predicate is available for
 *      future call-sites that want a one-liner.)
 *
 * Pure function — no side effects. Safe to call with any value; returns
 * `false` for null / non-axios shapes.
 */
export const isManagerPinFailure403 = (error) => {
  if (!error || typeof error !== "object") return false;
  const response = error.response;
  if (!response || response.status !== 403) return false;
  const data = response.data;
  if (!data || typeof data !== "object") return false;
  const code = data.code || data.error;
  return typeof code === "string" && code.startsWith("MANAGER_PIN_");
};

const clearStoredAuth = () => {
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
  window.dispatchEvent(new CustomEvent("auth-logged-out"));
};

const persistRefreshedSession = ({ token, refreshToken, expiresAt }) => {
  localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken);
  }
  if (expiresAt) {
    localStorage.setItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY, expiresAt);
  }

  window.dispatchEvent(
    new CustomEvent("auth-token-updated", {
      detail: { token, refreshToken, expiresAt },
    })
  );
};

const redirectToLogin = () => {
  if (window.location.pathname !== "/login") {
    const redirect = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/login?reason=session-expired&redirect=${encodeURIComponent(
      redirect
    )}`;
  }
};

const refreshAccessToken = async () => {
  if (refreshRequest) {
    return refreshRequest;
  }

  const refreshToken = localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  refreshRequest = axios
    .post(`${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, { refreshToken })
    .then((response) => {
      const session = {
        token: response.data.token,
        refreshToken: response.data.refreshToken,
        expiresAt: response.data.expiresAt,
      };
      persistRefreshedSession(session);
      return session;
    })
    .finally(() => {
      refreshRequest = null;
    });

  return refreshRequest;
};

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors.
//
// CORP-FAMILY-005 contract lock (2026-06-07):
//   - HTTP 401 = "session expired / not authenticated" → attempt silent
//     refresh, retry, and on continued failure clear auth + redirect to
//     /login. This is the existing behavior preserved verbatim below.
//   - HTTP 403 = "authenticated, but not authorized for THIS action" →
//     pass through to the originating call site as a normal rejected
//     Promise. The interceptor MUST NOT redirect on 403. Manager-PIN
//     failures (PR #205, post-`c095c09`) arrive as 403 with body
//     `{ code: 'MANAGER_PIN_INVALID' | 'MANAGER_PIN_NOT_PROVISIONED', ... }`
//     and the unlink/setup/cooldown-override modals render the inline
//     error from `error.response.data.code`. See `isManagerPinFailure403`
//     above for the predicate exposed to call sites.
//
// If a future maintainer needs to add a 403 branch (e.g., admin-only
// gating UX), add it ABOVE the 401 block and exclude 403s that satisfy
// `isManagerPinFailure403(error)` so PIN-failure UX continues to fall
// through to the modal handler.
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Get user-friendly error message
    const errorMessage = getErrorMessage(error);
    const originalRequest = error.config || {};

    // Handle authentication errors
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !String(originalRequest.url || "").includes(ENDPOINTS.AUTH.REFRESH) &&
      !String(originalRequest.url || "").includes(ENDPOINTS.AUTH.LOGIN)
    ) {
      originalRequest._retry = true;

      return refreshAccessToken()
        .then((session) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${session.token}`;
          return apiClient(originalRequest);
        })
        .catch((refreshError) => {
          logError(refreshError, "API Service refresh");
          const status = refreshError?.response?.status;
          if ([400, 401, 403].includes(status)) {
            clearStoredAuth();
            redirectToLogin();
          }
          return Promise.reject(refreshError);
        });
    }

    if (
      error.response &&
      error.response.status === 401 &&
      String(originalRequest.url || "").includes(ENDPOINTS.AUTH.REFRESH)
    ) {
      clearStoredAuth();
      redirectToLogin();
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !String(originalRequest.url || "").includes(ENDPOINTS.AUTH.LOGIN)
    ) {
      const hasRefreshToken = Boolean(
        localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY)
      );
      if (!hasRefreshToken && window.location.pathname !== "/login") {
        clearStoredAuth();
        redirectToLogin();
      }
    }

    // Log the error with context
    logError(error, "API Service");

    // Add error message to the error object
    error.userMessage = errorMessage;

    return Promise.reject(error);
  }
);

/**
 * API Endpoints organized by resource/domain
 */

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post(ENDPOINTS.AUTH.LOGIN, credentials),
  refreshSession: (refreshToken) =>
    apiClient.post(ENDPOINTS.AUTH.REFRESH, { refreshToken }),
  logout: (refreshToken) => apiClient.post(ENDPOINTS.AUTH.LOGOUT, { refreshToken }),
  register: (userData) => apiClient.post(ENDPOINTS.AUTH.REGISTER, userData),
  verifyEmail: (token) =>
    apiClient.get(`${ENDPOINTS.AUTH.VERIFY_EMAIL}/${token}`),
  requestPasswordReset: (email) =>
    apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email }),
  resetPassword: (token, password) =>
    apiClient.post(`${ENDPOINTS.AUTH.RESET_PASSWORD}/${token}`, { password }),
};

// User Profile API
export const profileAPI = {
  getProfile: () => apiClient.get(ENDPOINTS.PROFILE.GET),
  updateProfile: (profileData) =>
    apiClient.put(ENDPOINTS.PROFILE.UPDATE, profileData),
  uploadProfileImage: (formData) =>
    apiClient.post(ENDPOINTS.PROFILE.UPLOAD_IMAGE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  uploadProfilePicture: (formData) =>
    apiClient.post(ENDPOINTS.PROFILE.UPLOAD_IMAGE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  // The member's credits with the booking rules (rows, totals,
  // recentlyExpired, addOns). Backend: GET /api/profile/credit-summary.
  getCreditSummary: () => apiClient.get("/profile/credit-summary"),
};

// Bookings API
export const bookingsAPI = {
  getAvailableSessions: (date, sessionType) =>
    apiClient.get(ENDPOINTS.SESSIONS.AVAILABLE, {
      params: { date, sessionType },
    }),
  getMyBookings: () => apiClient.get(ENDPOINTS.SESSIONS.MY_BOOKINGS),
  bookSession: (bookingData) =>
    apiClient.post(ENDPOINTS.SESSIONS.CREATE, bookingData),
  updateBooking: (bookingId, updateData) =>
    apiClient.put(`${ENDPOINTS.SESSIONS.UPDATE}/${bookingId}`, updateData),
  cancelBooking: (bookingId) =>
    apiClient.delete(`${ENDPOINTS.SESSIONS.CANCEL}/${bookingId}/cancel`),
};

// Memberships API
export const membershipsAPI = {
  getMemberships: () => apiClient.get(ENDPOINTS.PRODUCTS.MEMBERSHIPS),
  getMembershipStatus: () => apiClient.get("/membership-status"),
  getDropIns: () => apiClient.get(ENDPOINTS.PRODUCTS.DROP_INS),
  getPackages: () => apiClient.get(ENDPOINTS.PRODUCTS.PACKAGES),
};

// Intro Offer API (INTRO-OFFER-001)
// The customer-facing read surface is the additive `introOffer` block on
// /api/profile (Story A). This module wraps only the POST claim endpoint
// (Story B). On 200 success the caller should refresh /api/profile via
// profileAPI.getProfile() to pick up the updated state + displayCopy.
export const introOfferAPI = {
  claim: () => apiClient.post(ENDPOINTS.INTRO_OFFER.CLAIM, {}),
};

// Payments API
export const paymentsAPI = {
  createSetupIntent: () => apiClient.post(ENDPOINTS.PAYMENTS.SETUP_INTENT),
  savePaymentMethod: (paymentMethodId) =>
    apiClient.post(ENDPOINTS.PAYMENTS.SAVE_METHOD, { paymentMethodId }),
  getPaymentMethods: () => apiClient.get(ENDPOINTS.PAYMENTS.GET_METHODS),
  deletePaymentMethod: (paymentMethodId) =>
    apiClient.post(ENDPOINTS.PAYMENTS.DELETE_METHOD, { paymentMethodId }),
};

// Stripe API
export const stripeAPI = {
  createCheckoutSession: (productData) =>
    apiClient.post(ENDPOINTS.STRIPE.CHECKOUT, productData),
  createPortalSession: (userId) =>
    apiClient.post(ENDPOINTS.STRIPE.PORTAL, { userId }),
};

// PROMO-RF (hotfix 2026-06-20): the legacy in-house `promoCodeAPI` export
// was removed. The BE legacy `/api/promo-codes` mount has been unmounted
// since the Stripe-authoritative pass-through landed (backend/index.js:561-567);
// the legacy methods (`getAllPromoCodes`, `getPromoCodeById`, `createPromoCode`,
// `updatePromoCode`, `deletePromoCode`, `validatePromoCode`, `applyPromoCode`,
// `syncWithStripe`) all 404'd in production. The sole FE consumer
// (PromoCodeManagement.js / `/staff/promo-codes`) is now redirected to the
// deployed Stripe-authoritative `StaffPromoManagementPage` (`/staff/promo-management`,
// via `staffPromoAPI` at line ~1761 below). NO REPLACEMENT EXPORT IS NEEDED.
// Note: the POS cart `terminalAPI.applyDiscount` below still calls
// `/promo-codes/apply` as a string literal (separate consumer, separate
// concern — flagged for follow-up to migrate to the RF-C/D `/api/promo`
// surface; NOT in scope for this hotfix because the POS money path requires
// its own validation envelope).

// PROMO-RF-C dual-source validate-at-cart (ADR-029 D3/D5).
//
// Terminal API for Stripe Reader S700
export const terminalAPI = {
  // Location management
  createLocation: (locationData) =>
    apiClient.post("/terminal/locations", locationData),
  listLocations: () => apiClient.get("/terminal/locations"),
  getLocationById: (locationId) =>
    apiClient.get(`/terminal/locations/${locationId}`),
  updateLocation: (locationId, locationData) =>
    apiClient.put(`/terminal/locations/${locationId}`, locationData),

  // Reader management
  createConnectionToken: () => apiClient.post("/terminal/connection-token"),
  registerReader: (readerData) =>
    apiClient.post("/terminal/readers", readerData),
  listReaders: (locationId) =>
    apiClient.get(
      "/terminal/readers",
      locationId ? { params: { location_id: locationId } } : {}
    ),
  findRegisteredReaders: (locationId) =>
    apiClient.get("/terminal/readers/registered", {
      params: { location_id: locationId },
    }),
  getReaderById: (readerId) => apiClient.get(`/terminal/readers/${readerId}`),
  updateReader: (readerId, readerData) =>
    apiClient.put(`/terminal/readers/${readerId}`, readerData),
  updateReaderStatus: (statusData) =>
    apiClient.post("/terminal/readers/status", statusData),
  displayCartOnTerminal: (cartData) =>
    apiClient.post("/terminal/readers/display-cart", cartData),
  // Clears a cart shown on the reader. The server never cancels a payment
  // that is waiting on the reader (it answers skipped: payment_in_progress).
  clearReaderDisplay: (readerId) =>
    apiClient.post("/terminal/readers/clear-display", { readerId }),

  // Payment operations
  createPaymentIntent: (paymentData) =>
    apiClient.post("/terminal/payment-intents", paymentData),
  createTerminalPayment: (terminalPaymentData) =>
    apiClient.post("/terminal/process-payment", terminalPaymentData),
  // Gift-card POS path: creates a record-only TerminalTransaction without
  // requiring Stripe-Terminal coupling (payment_intent_id / reader_id).
  // The card-present /process-payment route above is unchanged.
  createGiftCardTransaction: (giftCardTransactionData) =>
    apiClient.post(
      "/terminal/gift-card-transactions",
      giftCardTransactionData
    ),
  retrievePaymentIntent: (paymentIntentId) =>
    apiClient.get(`/terminal/payment-intents/${paymentIntentId}`),
  // POS-PAYMENTINTENT-CANCEL-001 (ADR-034 §"Decision" item 5) — `reason`
  // is now a REQUIRED body param. The backend enforces validation against
  // the documented enum (tender_switch_to_gift_card,
  // tender_switch_to_cash, customer_abandoned, card_present_failure_recovery,
  // staff_change_payment_method). Callers MUST pass one of these values.
  // Backwards-compat note: this was previously a parameterless POST that
  // 404'd silently against an unmounted backend route — there is no
  // production traffic to preserve for the old shape.
  cancelPaymentIntent: (paymentIntentId, reason) =>
    apiClient.post(`/terminal/payment-intents/${paymentIntentId}/cancel`, {
      reason,
    }),
  capturePayment: (captureData) =>
    apiClient.post("/terminal/capture-payment", captureData),
  // Issue #289 — one clear payment state (succeeded, failed, canceled,
  // pending); the server delivers the sale once when it has succeeded.
  getPosPaymentStatus: (paymentIntentId) =>
    apiClient.get(`/terminal/pos-payments/${paymentIntentId}/status`),
  // Test mode only (staging): tap a test card on a simulated reader.
  simulatePosTap: (paymentIntentId) =>
    apiClient.post(`/terminal/pos-payments/${paymentIntentId}/simulate-tap`),
  // Issue #289: clear a stuck reader action (and an unpaid attempt on it).
  clearPosReader: (readerId) =>
    apiClient.post(`/terminal/readers/${readerId}/clear`),
  processRefund: (refundData) => apiClient.post("/terminal/refund", refundData),
  createReceipt: (receiptData) =>
    apiClient.post("/terminal/receipt", receiptData),
  collectTip: (paymentIntentId, tipData) =>
    apiClient.post(
      `/terminal/payment-intents/${paymentIntentId}/collect-tip`,
      tipData
    ),

  // Product management for POS
  getProductsForPOS: () => apiClient.get("/terminal/products"),
  syncProductsFromStripe: () => apiClient.post("/terminal/products/sync"),
  getProductById: (productId) =>
    apiClient.get(`/terminal/products/${productId}`),
  updateProductInventory: (productId, inventoryData) =>
    apiClient.put(`/terminal/products/${productId}/inventory`, inventoryData),

  // Discount management
  // PROMO-RF-H1 rewired onto Stripe-authoritative path
  // (`/terminal/discounts/apply` -> productController.applyDiscount ->
  // classifyStripePromo). Legacy `/promo-codes/apply` mount removed
  // when in-house PromoCode engine was cut over (PR #188 / RF-H2).
  applyDiscount: (discountData) =>
    apiClient
      .post("/terminal/discounts/apply", {
        code: discountData.code,
        subtotal: discountData.subtotal || discountData.amount,
        cartItems: discountData.cartItems,
        productType: discountData.productType || undefined,
      })
      .then((response) => {
        // Normalize the response format
        if (response.data.data && response.data.data.discount) {
          // If response is from promoCodeController
          const discountInfo = response.data.data.discount;
          response.data.discountAmount = discountInfo.discountAmount;
          response.data.discountCode = response.data.data.promoCode.code;
        }
        return response;
      }),
  listDiscounts: () => apiClient.get("/terminal/discounts"),
  createDiscount: (discountData) =>
    apiClient.post("/terminal/discounts", discountData),

  // Transaction management
  getTransactions: (params) =>
    apiClient.get("/terminal/transactions", { params }),
  getTransactionById: (transactionId) =>
    apiClient.get(`/terminal/transactions/${transactionId}`),
  voidTransaction: (transactionId) =>
    apiClient.post(`/terminal/transactions/${transactionId}/void`),

  // Analytics
  getTransactionAnalytics: (params) =>
    apiClient.get("/terminal/analytics", { params }),
  getReaderAnalytics: (readerId, params) =>
    apiClient.get(`/terminal/analytics/readers/${readerId}`, { params }),
  getLocationAnalytics: (locationId, params) =>
    apiClient.get(`/terminal/analytics/locations/${locationId}`, { params }),
  getStaffAnalytics: (staffId, params) =>
    apiClient.get(`/terminal/analytics/staff/${staffId}`, { params }),
  // Gift card operations
  validateGiftCard: (giftCardData) =>
    apiClient.post("/terminal/gift-cards/validate", giftCardData),
  getGiftCardBalance: (code) =>
    apiClient.get(`/terminal/gift-cards/${code}/balance`),
  redeemGiftCard: (redemptionData) =>
    apiClient.post("/terminal/gift-cards/redeem", redemptionData),
  cancelRedemption: (transactionId) =>
    apiClient.delete(`/terminal/gift-cards/redemption/${transactionId}`),
  getGiftCardHistory: (code) =>
    apiClient.get(`/terminal/gift-cards/${code}/history`),

  // Proration
  calculateProratedAmount: (data) =>
    apiClient.post("/terminal/public/calculate-proration", data),
};

// Staff API
export const staffAPI = {
  // Customer management
  searchCustomers: (params) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/search`, { params }),
  getCustomers: (params) =>
    apiClient.get(ENDPOINTS.STAFF.CUSTOMERS, { params }),
  getCustomerById: (customerId) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}`),
  // Credit sources a booking would use (legacy and shared), in priority order.
  getCustomerCreditSources: (customerId, sessionType, date) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/credit-sources`, {
      params: { sessionType, date },
    }),
  updateCustomer: (customerId, profileData) =>
    apiClient.put(`/staff/customers/${customerId}`, profileData),
  updateCustomerMembershipPause: (customerId, pauseData) =>
    apiClient.patch(`/staff/customers/${customerId}/membership-pause`, pauseData),
  resumeCustomerMembership: (customerId) =>
    apiClient.post(`/staff/customers/${customerId}/membership-resume`),
  updateCustomerStatus: (customerId, statusData) =>
    apiClient.patch(`/staff/customers/${customerId}/status`, statusData),
  uploadCustomerProfilePicture: (customerId, formData) =>
    apiClient.post(`/staff/customers/${customerId}/profile-picture`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteCustomerProfilePicture: (customerId) =>
    apiClient.delete(`/staff/customers/${customerId}/profile-picture`),
  // Get customer payment method status
  getCustomerPaymentStatus: (customerId) =>
    apiClient.get(`/staff/customers/${customerId}/payment-status`),

  // Create customer portal session for staff access
  createCustomerPortalSession: (customerId) =>
    apiClient.post(`/staff/customers/${customerId}/portal-session`),
  getCustomerAuditLog: (customerId, params) =>
    apiClient.get(`/staff/customers/${customerId}/audit-log`, { params }),
  getCustomerBookings: (customerId, params) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/bookings`, {
      params,
    }),
  bookSessionForUser: (bookingData) =>
    apiClient.post(`/sessions/staff-book`, bookingData),
  // Staff booking paid on the POS card reader (Stripe Terminal). The server
  // computes the amount; the form polls the status, then books with the
  // PaymentIntent ID.
  listCardReaders: () => apiClient.get("/terminal/readers"),
  // Issue #289: clear a reader stuck with an old action (and an unpaid
  // payment on it); a paid payment is never cancelled.
  clearCardReader: (readerId) =>
    apiClient.post(`/terminal/readers/${readerId}/clear`),
  startBookingTerminalPayment: (paymentData) =>
    apiClient.post(`/sessions/staff-book/terminal-payment`, paymentData),
  getBookingTerminalPayment: (paymentIntentId) =>
    apiClient.get(`/sessions/staff-book/terminal-payment/${paymentIntentId}`),
  cancelBookingTerminalPayment: (paymentIntentId) =>
    apiClient.post(
      `/sessions/staff-book/terminal-payment/${paymentIntentId}/cancel`
    ),
  refundBookingTerminalPayment: (paymentIntentId) =>
    apiClient.post(
      `/sessions/staff-book/terminal-payment/${paymentIntentId}/refund`
    ),
  simulateBookingTerminalTap: (paymentIntentId) =>
    apiClient.post(
      `/sessions/staff-book/terminal-payment/${paymentIntentId}/simulate-tap`
    ),
  // Credits by the profile display rule (server-side), the source of the
  // Session Credit Summary table and the Credit Balance card.
  getCustomerCreditSummary: (customerId) =>
    apiClient.get(`/staff/customers/${customerId}/credit-summary`),
  getCustomerBookingStats: (customerId) =>
    apiClient.get(`/staff/customers/${customerId}/booking-stats`),
  getCustomerInteractions: (customerId, params) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/interactions`, {
      params,
    }),
  getCustomerFeedback: (customerId, params) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/feedback`, {
      params,
    }),
  getCustomerCommunications: (customerId, params) =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/communications`, {
      params,
    }),
  getNotificationCategories: () =>
    apiClient.get("/staff/notification-subscriptions/categories"),
  getNotificationSubscriptions: () =>
    apiClient.get("/staff/notification-subscriptions"),
  createNotificationSubscription: (subscriptionData) =>
    apiClient.post("/staff/notification-subscriptions", subscriptionData),
  updateNotificationSubscription: (subscriptionId, subscriptionData) =>
    apiClient.put(
      `/staff/notification-subscriptions/${subscriptionId}`,
      subscriptionData
    ),
  deleteNotificationSubscription: (subscriptionId) =>
    apiClient.delete(`/staff/notification-subscriptions/${subscriptionId}`),
  addCustomerTag: (customerId, tagData) =>
    apiClient.post(`${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/tags`, tagData),
  removeCustomerTag: (customerId, tagId) =>
    apiClient.delete(
      `${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/tags/${tagId}`
    ),
  createMember: (memberData) =>
    apiClient.post(`${ENDPOINTS.STAFF.MEMBERS}`, memberData),
  createAccount: (accountData) =>
    apiClient.post("/staff/accounts", accountData),
  updateMemberSessions: (customerId, sessionData) =>
    apiClient.put(`/staff/customers/${customerId}/sessions`, sessionData),

  // Booking restrictions (BOOKING-RESTRICT-003 — admin-only; per Marcus tech-doc §3.3 + Zuri spec §4.2.
  // Full-replace PUT semantics: the request body IS the new state in its entirety.
  // Hard Rule §: these endpoints are staff-only; the response shape MUST NEVER be returned
  // to a customer-authenticated caller — see /api/profile, /api/sessions/availability,
  // /api/sessions/myBookings, /api/members/membership-status for the negative-leak invariant.)
  getBookingRestrictions: (customerId) =>
    apiClient.get(
      `${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/booking-restrictions`
    ),
  setBookingRestrictions: (customerId, payload) =>
    apiClient.put(
      `${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/booking-restrictions`,
      payload
    ),
  removeBookingRestrictions: (customerId) =>
    apiClient.delete(
      `${ENDPOINTS.STAFF.CUSTOMERS}/${customerId}/booking-restrictions`
    ),
  // Pickup-source for the suite picker in the BOOKING-RESTRICT-003 management modal.
  // Returns all `Suite.status === "active"` suites with their Mongo _id + type + number + name,
  // grouped client-side by type. Pure-additive backend endpoint per the tech-doc §4 picker spec.
  // (Awaiting backend-engineer.md MSG-140 endpoint landing; FE consumes the contract once live.)
  getActiveSuites: () => apiClient.get(`/staff/suites/active`),

  // Bookings management
  getBookings: (params) =>
    apiClient.get(`${ENDPOINTS.STAFF.BOOKINGS}`, { params }),
  getBookingById: (bookingId) =>
    apiClient.get(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}`),
  updateBooking: (bookingId, bookingData) =>
    apiClient.put(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}`, bookingData),
  completeBooking: (bookingId) =>
    apiClient.post(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/complete`),
  checkInBooking: (bookingId) =>
    apiClient.post(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/checkin`),
  checkInAndStartBooking: (bookingId, customDuration) =>
    apiClient.post(
      `${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/checkin-and-start`,
      customDuration ? { customDuration } : {}
    ),
  startSession: (bookingId, customDuration) =>
    apiClient.post(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/start`, {
      customDuration,
    }),
  updateSessionDuration: (bookingId, duration) =>
    apiClient.put(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/duration`, {
      duration,
    }),
  cancelBooking: (bookingId) =>
    apiClient.delete(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}`),
  applyCancellationFee: (bookingId) =>
    apiClient.post(`${ENDPOINTS.STAFF.BOOKINGS}/${bookingId}/apply-fee`),

  // Corporate programs
  getOrganizations: () => apiClient.get("/staff/organizations"),
  createOrganization: (organizationData) =>
    apiClient.post("/staff/organizations", organizationData),
  createOrganizationProgram: (organizationId, programData) =>
    apiClient.post(`/staff/organizations/${organizationId}/programs`, programData),
  getOrganizationDomainRules: (organizationId) =>
    apiClient.get(`/staff/organizations/${organizationId}/domain-rules`),
  createOrganizationDomainRule: (organizationId, domainRuleData) =>
    apiClient.post(
      `/staff/organizations/${organizationId}/domain-rules`,
      domainRuleData
    ),
  updateOrganizationDomainRule: (organizationId, domainRuleId, domainRuleData) =>
    apiClient.put(
      `/staff/organizations/${organizationId}/domain-rules/${domainRuleId}`,
      domainRuleData
    ),
  enrollOrganizationUser: (organizationId, programId, enrollmentData) =>
    apiClient.post(
      `/staff/organizations/${organizationId}/programs/${programId}/enrollments`,
      enrollmentData
    ),
  getOrganizationUsage: (organizationId, params) =>
    apiClient.get(`/staff/organizations/${organizationId}/usage`, { params }),
  getOrganizationEnrollments: (organizationId) =>
    apiClient.get(`/staff/organizations/${organizationId}/enrollments`),

  // CORP-FAMILY-002 — staff link/unlink family member + AuditLog query.
  // Endpoints land in CORP-FAMILY-001 BE (Simone). Contract per ADR-037 D2
  // + ADR-039 AuditLog event taxonomy. NEVER log managerPin client-side.
  linkOrganizationEnrollmentFamilyMember: (enrollmentId, payload) =>
    apiClient.post(
      `/staff/organizations/enrollments/${enrollmentId}/family-member/link`,
      payload
    ),
  unlinkOrganizationEnrollmentFamilyMember: (enrollmentId, payload) =>
    apiClient.post(
      `/staff/organizations/enrollments/${enrollmentId}/family-member/unlink`,
      payload
    ),
  // CORP-FAMILY-003 — admin cooldown override (PR #207 BE). Bypasses the
  // ADR-037 D1 90-day anti-musical-chairs guard so a new family member can
  // be linked immediately. `managerPin` validated by `requireManagerPin`
  // middleware on the BE; `reason` REQUIRED for the AuditLog
  // `corp-family:cooldown-overridden` row (Roderick's quarterly review).
  // Failure modes: 403 MANAGER_PIN_INVALID / MANAGER_PIN_NOT_PROVISIONED /
  // MANAGER_PIN_FORBIDDEN_ROLE — surface inline per `isManagerPinFailure403`
  // predicate (PR #206 CORP-FAMILY-005). 422
  // CORP_FAMILY_NO_COOLDOWN_TO_OVERRIDE on idempotency guard miss;
  // 400 CORP_FAMILY_COOLDOWN_OVERRIDE_REASON_REQUIRED on FE-bypass.
  overrideOrganizationEnrollmentFamilyMemberCooldown: (enrollmentId, payload) =>
    apiClient.post(
      `/staff/organizations/enrollments/${enrollmentId}/family-member/cooldown-override`,
      payload
    ),
  getOrganizationFamilyAuditLog: (organizationId, params) =>
    apiClient.get(
      `/staff/organizations/${organizationId}/audit-log/corp-family`,
      { params }
    ),

  // CORP-PARTNERSHIP-SUSPEND-REACTIVATE-001 (Sprint 19) — corp-partnership
  // lifecycle state transitions. Per ADR-049 D1: `active <-> suspended` is
  // reversible metadata + guard-clause flip; existing enrollments + bookings
  // + credits are UNTOUCHED. Reason is OPTIONAL on suspend (D9 access
  // control: reason NOT required for reversible actions); when provided
  // BE enforces >= 10 chars for the audit-log row.
  //
  // Failure modes:
  //  - 409 CORP_PROGRAM_ALREADY_SUSPENDED / CORP_PROGRAM_ALREADY_ACTIVE
  //    (idempotency guard per D10)
  //  - 400 CORP_PROGRAM_INVALID_TRANSITION (from cancelled path — not exercised
  //    by Story 1, but surface predicate must render generically)
  //  - 403 admin-only auth failure (D9)
  //
  // Story 2 (HARD-CANCEL) + Story 5 (RESTORE) land their own endpoints
  // when those PRs ship.
  suspendOrganizationProgram: (organizationId, programId, payload) =>
    apiClient.post(
      `/staff/organizations/${organizationId}/programs/${programId}/suspend`,
      payload || {}
    ),
  reactivateOrganizationProgram: (organizationId, programId, payload) =>
    apiClient.post(
      `/staff/organizations/${organizationId}/programs/${programId}/reactivate`,
      payload || {}
    ),

  // CORP-PARTNERSHIP-HARD-CANCEL-CORE-001 (Sprint 19, Story 2) — corp-
  // partnership hard cancel. Per ADR-049 D3 (amended 2026-06-30): reason
  // REQUIRED (>= 10 chars per D6 audit-log convention); `deactivateAt`
  // null = immediate, ISO date = grace-period end (daily cron sweeps at
  // 12:00 AM MST); `bookingsTreatment` is always "auto-cancel" per Q1
  // amendment lock (convert-to-paid path DROPPED). Refunds + comms are
  // handled manually by admin (Q4 + Q7 amendment locks).
  //
  // Failure modes:
  //  - 409 { code: 'already_cancelled', lifecycleState: 'cancelled' }
  //    (idempotency guard per D10)
  //  - 400 { code: 'invalid_deactivate_at' } if grace date is in the past
  //  - 403 admin-only auth failure (D9)
  //
  // CFG=YES per PR template: this action flips customer-visible entitlement
  // (corp-rate access) and cancels future bookings; end-to-end staff smoke
  // is required before customer rollout.
  hardCancelOrganizationProgram: (organizationId, programId, payload) =>
    apiClient.post(
      `/staff/organizations/${organizationId}/programs/${programId}/cancel`,
      payload || {}
    ),

  getProviderServiceOfferings: () =>
    apiClient.get("/staff/providers/service-offerings"),
  getTherapistUsers: () => apiClient.get("/staff/providers/therapist-users"),
  initializeDefaultMassageServices: () =>
    apiClient.post("/staff/providers/service-offerings/initialize"),
  getProviders: () => apiClient.get("/staff/providers"),
  createProvider: (providerData) =>
    apiClient.post("/staff/providers", providerData),
  updateProvider: (providerId, providerData) =>
    apiClient.put(`/staff/providers/${providerId}`, providerData),
  getProviderAvailability: (providerId) =>
    apiClient.get(`/staff/providers/${providerId}/availability`),
  setProviderAvailability: (providerId, availabilityData) =>
    apiClient.put(`/staff/providers/${providerId}/availability`, availabilityData),
  setProviderAvailabilityOverrides: (providerId, overridesData) =>
    apiClient.put(
      `/staff/providers/${providerId}/availability-overrides`,
      overridesData
    ),
  previewMassageAvailability: (providerId, params) =>
    apiClient.get(`/staff/providers/${providerId}/massage-availability`, {
      params,
    }),

  // Analytics
  getMemberAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.MEMBERS, { params }),
  getBookingAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.BOOKINGS, { params }),
  getAccountAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.ACCOUNTS, { params }),
  getMassagePayoutAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.MASSAGE_PAYOUTS, { params }),
  getCommunicationAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.COMMUNICATIONS, { params }),
  getInteractionAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.INTERACTIONS, { params }),
  getCustomerAnalytics: () =>
    apiClient.get(`${ENDPOINTS.STAFF.CUSTOMERS}/analytics`),
  exportAnalytics: (params) =>
    apiClient.get(ENDPOINTS.ANALYTICS.EXPORT, {
      params,
      responseType: "blob",
    }),
};

export const therapistAPI = {
  getDashboard: () => apiClient.get(ENDPOINTS.THERAPIST.DASHBOARD),
  setAvailability: (availabilityData) =>
    apiClient.put(ENDPOINTS.THERAPIST.AVAILABILITY, availabilityData),
  setAvailabilityOverrides: (overridesData) =>
    apiClient.put(ENDPOINTS.THERAPIST.AVAILABILITY_OVERRIDES, overridesData),
  previewAvailability: (params) =>
    apiClient.get(ENDPOINTS.THERAPIST.PREVIEW_AVAILABILITY, { params }),
};

// CRM API
export const crmAPI = {
  // Customer interactions
  addInteraction: (interactionData) =>
    apiClient.post(ENDPOINTS.CRM.INTERACTIONS, interactionData),
  getInteractions: (params) =>
    apiClient.get(ENDPOINTS.CRM.INTERACTIONS, { params }),

  // Communication templates
  getTemplates: () => apiClient.get(ENDPOINTS.CRM.TEMPLATES),
  createTemplate: (templateData) =>
    apiClient.post(ENDPOINTS.CRM.TEMPLATES, templateData),
  updateTemplate: (templateId, templateData) =>
    apiClient.put(`${ENDPOINTS.CRM.TEMPLATES}/${templateId}`, templateData),
  deleteTemplate: (templateId) =>
    apiClient.delete(`${ENDPOINTS.CRM.TEMPLATES}/${templateId}`),

  // Customer segments
  getSegments: () => apiClient.get(ENDPOINTS.CRM.SEGMENTS),
  createSegment: (segmentData) =>
    apiClient.post(ENDPOINTS.CRM.SEGMENTS, segmentData),
  updateSegment: (segmentId, segmentData) =>
    apiClient.put(`${ENDPOINTS.CRM.SEGMENTS}/${segmentId}`, segmentData),
  deleteSegment: (segmentId) =>
    apiClient.delete(`${ENDPOINTS.CRM.SEGMENTS}/${segmentId}`),

  // Customer journeys
  getJourneys: () => apiClient.get(ENDPOINTS.CRM.JOURNEYS),
  createJourney: (journeyData) =>
    apiClient.post(ENDPOINTS.CRM.JOURNEYS, journeyData),
  updateJourney: (journeyId, journeyData) =>
    apiClient.put(`${ENDPOINTS.CRM.JOURNEYS}/${journeyId}`, journeyData),
  deleteJourney: (journeyId) =>
    apiClient.delete(`${ENDPOINTS.CRM.JOURNEYS}/${journeyId}`),
};

// Feedback API
export const feedbackAPI = {
  submitFeedback: (feedbackData) =>
    apiClient.post(ENDPOINTS.FEEDBACK.SUBMIT, feedbackData),
  getFeedback: (params) => apiClient.get(ENDPOINTS.FEEDBACK.GET, { params }),
  getPublicFeedback: (params) =>
    apiClient.get(`${ENDPOINTS.FEEDBACK.GET}/public`, { params }),
  respondToFeedback: (feedbackId, responseData) =>
    apiClient.post(
      `${ENDPOINTS.FEEDBACK.GET}/${feedbackId}/respond`,
      responseData
    ),
  reviewFeedback: (feedbackId, reviewData) =>
    apiClient.post(
      `${ENDPOINTS.FEEDBACK.GET}/${feedbackId}/review`,
      reviewData
    ),
  completeFollowUp: (feedbackId, followUpData) =>
    apiClient.post(
      `${ENDPOINTS.FEEDBACK.GET}/${feedbackId}/complete-followup`,
      followUpData
    ),
};

// Suites API
export const suitesAPI = {
  getSuites: () => apiClient.get(ENDPOINTS.SUITES.GET_ALL),
  getSuiteById: (suiteId) =>
    apiClient.get(`${ENDPOINTS.SUITES.GET_ONE}/${suiteId}`),
  getSuiteAvailability: (suiteId, date) =>
    apiClient.get(`${ENDPOINTS.SUITES.AVAILABILITY}/${suiteId}/availability`, {
      params: { date },
    }),
  getSaunaAvailability: (date, time, isAda) =>
    apiClient.get(`${ENDPOINTS.SUITES.GET_ALL}/sauna`, {
      params: { date, time, isAda },
    }),
  getRedLightAvailability: (date, time, isAda) =>
    apiClient.get(`${ENDPOINTS.SUITES.GET_ALL}/redlight`, {
      params: { date, time, isAda },
    }),
};

// Contact API
export const contactAPI = {
  submitContactForm: (contactData) =>
    apiClient.post(ENDPOINTS.CONTACT.SUBMIT, contactData),
};

// Operating Hours API
export const operatingHoursAPI = {
  getAllOperatingHours: () => apiClient.get("/operating-hours"),
  getOperatingHoursForDay: (dayOfWeek) =>
    apiClient.get(`/operating-hours/${dayOfWeek}`),
  setOperatingHours: (operatingHoursData) =>
    apiClient.post("/operating-hours", operatingHoursData),
  deleteOperatingHours: (dayOfWeek) =>
    apiClient.delete(`/operating-hours/${dayOfWeek}`),
};

// Blocked Times API
export const blockedTimesAPI = {
  getAllBlockedTimes: () => apiClient.get("/blocked-times"),
  getBlockedTimesForRange: (startDate, endDate) =>
    apiClient.get("/blocked-times/range", { params: { startDate, endDate } }),
  getBlockedTimeById: (id) => apiClient.get(`/blocked-times/${id}`),
  createBlockedTime: (blockedTimeData) =>
    apiClient.post("/blocked-times", blockedTimeData),
  updateBlockedTime: (id, blockedTimeData) =>
    apiClient.put(`/blocked-times/${id}`, blockedTimeData),
  deleteBlockedTime: (id) => apiClient.delete(`/blocked-times/${id}`),
};

// POS API
export const posAPI = {
  // Product management
  getProducts: () => apiClient.get("/products"),
  getProductById: (productId) => apiClient.get(`/products/${productId}`),
  createProduct: (productData) => apiClient.post("/products", productData),
  updateProduct: (productId, productData) =>
    apiClient.put(`/products/${productId}`, productData),
  deleteProduct: (productId) => apiClient.delete(`/products/${productId}`),
};

// Add-ons API
// Display prices for add-ons, drop-ins, and massages, read from Stripe.
export const pricingAPI = {
  getPricing: (date) =>
    apiClient.get("/pricing", { params: date ? { date } : {} }),
};

export const addOnsAPI = {
  getSummary: () => apiClient.get("/add-ons/summary"),
  useGuestPass: (bookingId, guestInfo) =>
    apiClient.post("/add-ons/use-guest-pass", { bookingId, guestInfo }),
  useHalotherapy: (bookingId) =>
    apiClient.post("/add-ons/use-halotherapy", { bookingId }),
  giftSession: (giftInfo) => apiClient.post("/add-ons/gift-session", giftInfo),
  checkRecipient: (email) => apiClient.get(`/add-ons/check-recipient/${email}`),
};

export const activityAPI = {
  getActivityFeed: (page = 1, limit = 20) =>
    apiClient.get("/activity", { params: { page, limit } }),
};

// Workforce — Staff Profile API (TIME-001)
// Backend contract per Simone's reconciliation 2026-04-28:
//   POST   /api/workforce/staff               — create + User-link
//   GET    /api/workforce/staff               — list (RBAC-filtered)
//   GET    /api/workforce/staff/:id           — detail
//   PUT    /api/workforce/staff/:id           — edit + rate-change AuditLog
//                                               (when body.hourlyRate changes,
//                                                include body.reason for audit)
//   DELETE /api/workforce/staff/:id           — soft-delete (sets active=false)
//   GET    /api/workforce/staff/me            — own-profile (staff role)
//   GET    /api/workforce/staff/:id/audit-log — rate-change history (admin)
// Reads against /api/staff/customers/search are reused for the User picker
// (existing endpoint — no new backend surface needed).
// hourlyRate is in CENTS on every payload (matches data-models §17.1).
export const staffProfileAPI = {
  list: (params) => apiClient.get("/workforce/staff", { params }),
  getById: (id) => apiClient.get(`/workforce/staff/${id}`),
  getOwn: () => apiClient.get("/workforce/staff/me"),
  create: (data) => apiClient.post("/workforce/staff", data),
  update: (id, data) => apiClient.put(`/workforce/staff/${id}`, data),
  deactivate: (id) => apiClient.delete(`/workforce/staff/${id}`),
  getRateHistory: (id) =>
    apiClient.get(`/workforce/staff/${id}/audit-log`, {
      params: { category: "workforce", action: "staff_rate_change" },
    }),
  // Picker-only User search — returns Users of ANY role (unlike the
  // member-only /staff/customers/search). Used by StaffProfileForm picker
  // when admin needs to link a new StaffProfile to an existing User.
  searchUsers: (term, limit = 10) =>
    apiClient.get("/workforce/staff/users/search", {
      params: { search: term, limit },
    }),
};

// Workforce — Job Code admin API (TIME-001 sub-scope per option (a))
// Backend contract per Simone's implementation 2026-04-28:
//   GET    /api/workforce/job-codes        — list (admin + staff for picker)
//   POST   /api/workforce/job-codes        — create. Body: { code, displayName }
//                                            (code = machine slug; displayName
//                                            = admin-friendly label)
//   PUT    /api/workforce/job-codes/:code  — rename. Body: { displayName }
//   DELETE /api/workforce/job-codes/:code  — soft-delete (sets active=false)
// Free-text add, no hierarchy; soft-delete only (preserves historical TimeLog
// references per data-models §17.4.1).
export const jobCodeAPI = {
  list: (params) => apiClient.get("/workforce/job-codes", { params }),
  create: (data) => apiClient.post("/workforce/job-codes", data),
  update: (code, data) =>
    apiClient.put(`/workforce/job-codes/${encodeURIComponent(code)}`, data),
  remove: (code) =>
    apiClient.delete(`/workforce/job-codes/${encodeURIComponent(code)}`),
};

// Workforce — TimeLog clock-in / clock-out / open-shift API (TIME-004a)
//
//   POST   /api/workforce/timelogs/clock-in   — body { coordinates, accuracy, jobCode?, clientType: 'web' }
//   POST   /api/workforce/timelogs/clock-out  — body { coordinates, accuracy, clientType: 'web' }
//   GET    /api/workforce/timelogs/me/open    — returns { openTimeLog: {...} | null }
//
// Contract authoritative source: docs/architecture/TIME-004a-clock-in-out-tech.md §6.
// Polling cadence for getOpenShift: 30s while page focused (callers pause on
// visibilitychange).
//
// Error-code surface (server returns { error: '<code>', ...details }):
//   clock-in:  staff_profile_not_found, invalid_job_code, missing_coordinates,
//              geofence_rejection (with distanceMeters + radiusMeters),
//              already_clocked_in (with openTimeLogId + punchInTime),
//              studio_geo_not_configured
//   clock-out: no_open_shift, geofence_rejection_clock_out, studio_geo_not_configured,
//              invalid_job_code (TIME-006-V2 — when staff submits a non-active
//              code that is NOT the row's punch-in code)
//
// TIME-006-V2: clockOut accepts optional `jobCode` body field. When present
// AND it differs from the open shift's punch-in `jobCode`, the backend
// overwrites `TimeLog.jobCode`, sets `jobCodeChangedAt = clockOutAt`, and
// writes an AuditLog row with action `timelog_jobcode_changed_at_clock_out`
// + reason `staff_clockout_jobcode_change` (Marcus tech doc §3.1; deployed-
// convention naming per Darius). Omitting the field, or sending a value
// equal to the punch-in code, takes the no-op path: jobCode unchanged,
// jobCodeChangedAt stays null, no audit row.
export const timeLogAPI = {
  clockIn: (payload) =>
    apiClient.post("/workforce/timelogs/clock-in", payload),
  clockOut: (payload) =>
    apiClient.post("/workforce/timelogs/clock-out", payload),
  getOpenShift: () => apiClient.get("/workforce/timelogs/me/open"),

  // TIME-009 — admin daily-review dashboard. Backend contract per Simone's
  // 2026-04-28 announcement: GET /api/workforce/timelogs/currently-clocked-in,
  // admin-only RBAC, returns the set of TimeLogs with status === 'open' (i.e.,
  // punchOutTime === null) joined to staff identity. Optional query params
  // (search, role) are passed through; backend filters server-side OR the
  // client filters in-memory — coordinated with Simone's response shape.
  getCurrentlyClocked: (params) =>
    apiClient.get("/workforce/timelogs/currently-clocked-in", { params }),

  // TIME-005 — break start / end. Mounted on the same `/staff/clock` screen
  // as TIME-004a. Both endpoints take no body; the server resolves "the
  // currently open shift for this user" from the JWT and either appends a
  // new entry to `breaks[]` (start) or sets `breakEnd` on the latest open
  // entry (end). The combined "End break and clock out" flow does NOT call
  // these endpoints — it calls `clockOut` and the server atomically ends
  // any open break (TIME-004a backend behavior).
  //
  // Error-code surface (server returns { error: '<code>' }):
  //   start: no_open_shift, already_on_break
  //   end:   no_open_shift, not_on_break
  //
  // Authoritative spec: docs/design/TIME-005-design.md §"Components" /
  // §"Cross-stack note for Marcus". Path follows the established plural
  // `/workforce/timelogs/...` convention; if Simone's backend PR ships a
  // different path, update both lines below in lockstep.
  startBreak: () =>
    apiClient.post("/workforce/timelogs/breaks/start"),
  endBreak: () =>
    apiClient.post("/workforce/timelogs/breaks/end"),
};

// Workforce — PayPeriod admin API (PAY-001 reads + PAY-005-owned writes)
//
// Backend contract source-of-truth (Sprint 1 retro action item 6 contract-
// snapshot convention): Marcus's PAY-001 tech doc §4 captured-curl JSON
// snapshots. This client cites EXACT keys from those snapshots, NOT prose.
//
// Reads (PAY-001 — LIVE in main as of `42db32b` PR #54):
//   POST   /api/workforce/pay-periods/close          — body { asOfDate, studioLocationCode? }
//   GET    /api/workforce/pay-periods/current        — query studioLocationCode? (optional)
//   GET    /api/workforce/pay-periods/:id            — drill-in entry-point
//   GET    /api/workforce/pay-periods/:id/timelogs   — query staffProfileId? status?
//
// Writes (PAY-005 — Simone authors backend in PAY-005 cluster; contract per
// PAY-005 design spec §9.3.1 / §9.3.2; Marcus confirmed option (b) "Simone
// authors fresh from §9.3" via architect.md MSG-087 reply 2026-05-02 18:10):
//   PUT    /api/workforce/pay-periods/:id/transition — body { to: 'Reviewed' | 'Approved' | 'Closed' }
//   PUT    /api/workforce/timelogs/:id/status        — body { status: 'pending' | 'approved' | 'rejected', rejectionReason? }
//
// Status enum capitalization (deliberate, Zuri spec §9.3 + Marcus MSG-087
// confirmation): PayPeriod.status is TitleCase ('Open' | 'Closed' | ...);
// TimeLog row status is lowercase ('pending' | 'approved' | 'rejected').
// Period transition body uses TitleCase to match PayPeriod; row status body
// uses lowercase to match TimeLog. Do not normalize.
//
// Error-code surface (server returns { error: '<code>', ...details }):
//   close:      forbidden_admin_only, invalid_as_of_date, studio_location_required,
//               studio_location_not_found, studio_location_not_configured,
//               already_closed (with payPeriodId + status),
//               transaction_failed, server_error
//   current/:id/timelogs: forbidden_admin_only, invalid_period_id,
//               pay_period_not_found, server_error
export const payPeriodAPI = {
  // PAY-001 reads (live)
  close: (data) => apiClient.post("/workforce/pay-periods/close", data),
  getCurrent: (params) =>
    apiClient.get("/workforce/pay-periods/current", { params }),
  getById: (id) => apiClient.get(`/workforce/pay-periods/${id}`),
  getTimeLogs: (id, params) =>
    apiClient.get(`/workforce/pay-periods/${id}/timelogs`, { params }),

  // WORKFORCE-PAY-PERIOD-PICKER-001 — list endpoint (admin-only on BE).
  // Query params per Simone's BE contract:
  //   status=Open|Closed|Reviewed|Approved|Exported|all (default: all)
  //   limit  (default 12, max 50)
  // Response shape: { payPeriods: [...], total: <unfiltered-for-status-count> }
  // Sort: descending by periodStart.
  // FE-side fallback: caller handles 404 (BE not yet deployed) by collapsing
  // the period picker to current-period-only (preserves today's UX exactly).
  list: (params) =>
    apiClient.get("/workforce/pay-periods", { params }),

  // PAY-005-owned writes (contract per spec §9.3; backend pending in Simone's
  // PAY-005 lane). UI ships against the published contract; integration
  // verification gates on Simone's PAY-005 backend PR.
  transition: (id, to) =>
    apiClient.put(`/workforce/pay-periods/${id}/transition`, { to }),
  updateTimeLogStatus: (timeLogId, payload) =>
    apiClient.put(`/workforce/timelogs/${timeLogId}/status`, payload),

  // PAY-007 — CSV export of a Locked (Approved or Exported) pay period.
  // Backend: GET /api/workforce/pay-periods/:id/export/csv
  // Returns a binary text/csv body with a Content-Disposition filename header
  // and X-Export-Schema-Version + X-Export-Hash audit headers. We request
  // `responseType: 'blob'` so axios hands back a Blob suitable for an
  // anchor-tag download trigger. Side effect: first export of an `Approved`
  // period flips status to `Exported` server-side; caller should refetch
  // period state after a successful response.
  exportCsv: (id) =>
    apiClient.get(`/workforce/pay-periods/${id}/export/csv`, {
      responseType: "blob",
    }),
};

// Workforce — TimeLog Correction Requests (TIME-010)
//
// Backend contract source-of-truth (Sprint 1 retro action item 6 contract-
// snapshot convention): Marcus's TIME-010 tech doc §4.1-§4.5 captured-curl
// JSON snapshots. This client cites EXACT keys from those snapshots.
//
//   POST   /api/workforce/timelog-corrections                 — submit (staff)
//   GET    /api/workforce/timelog-corrections                 — list (staff own / admin all)
//   PUT    /api/workforce/timelog-corrections/:id/approve     — approve (admin)
//   PUT    /api/workforce/timelog-corrections/:id/reject      — reject (admin, decisionReason ≥10 chars)
//   PUT    /api/workforce/timelogs/:id/admin-edit             — direct edit (admin, HIGH-risk)
//
// Response envelopes (from Marcus tech doc captured-curl):
//   POST submit:    { correctionRequest: {...} }                          (201)
//   GET list:       { corrections: [...], count, truncated }              (200)
//   PUT approve:    { correctionRequest: {...}, timeLog: {...} }          (200)
//   PUT reject:     { correctionRequest: {...} }                          (200; no timeLog)
//   PUT admin-edit: { timeLog: {...} }                                    (200)
//
// Status enum capitalization (deliberate per §3.1 + §3.2):
//   TimeLogCorrectionRequest.status is TitleCase ('Pending' | 'Approved' |
//   'Rejected' | 'Withdrawn'). TimeLog.source is lowercase including the
//   NEW 'manual_correction' value (§4.3 contract).
//
// Error-code surface (server returns { error: '<code>', ...details }):
//   submit:     reason_too_short (minLength: 10), reason_too_long (maxLength: 500),
//               invalid_target_date, invalid_intended_punch_in_time,
//               invalid_time_range, target_timelog_not_found,
//               target_timelog_not_owned, staff_profile_not_found,
//               period_locked (currentPeriodStatus, payPeriodId)
//   list:       invalid_status_filter, invalid_date_filter, staff_profile_not_found
//   approve:    invalid_request_id, invalid_job_code, decision_reason_too_long,
//               correction_request_not_found, target_timelog_not_found,
//               period_locked, invalid_transition (currentStatus,
//               allowedTransitions), forbidden_admin_only
//   reject:     invalid_request_id, decision_reason_required (minLength: 10),
//               decision_reason_too_long, correction_request_not_found,
//               invalid_transition, forbidden_admin_only
//   admin-edit: invalid_timelog_id, no_changes_supplied, reason_too_short,
//               reason_too_long, invalid_field_shape (field), invalid_time_range,
//               invalid_job_code, timelog_not_found, period_locked,
//               forbidden_admin_only
export const timelogCorrectionAPI = {
  // Staff submits a missing-punch (targetTimeLogId === null) or correction
  // (targetTimeLogId !== null) request.
  // Body: { targetTimeLogId, targetDate, intendedPunchInTime,
  //         intendedPunchOutTime, intendedJobCode, reason }
  submit: (payload) =>
    apiClient.post("/workforce/timelog-corrections", payload),

  // List correction requests. Admin sees all (with filters); staff scoped
  // server-side to own (server forces requestedBy: staffProfile._id).
  // Query params: { status?, requestedBy? (admin-only), dateFrom?, dateTo? }
  list: (params) =>
    apiClient.get("/workforce/timelog-corrections", { params }),

  // Admin approve. Body { decisionReason } where decisionReason is OPTIONAL
  // (≤500 chars when supplied). Response 200 carries BOTH the updated request
  // AND the resulting TimeLog (saves a round-trip on the post-success toast
  // [View →] link per Zuri spec §6.1).
  approve: (id, payload = {}) =>
    apiClient.put(`/workforce/timelog-corrections/${id}/approve`, payload),

  // Admin reject. Body { decisionReason } REQUIRED (≥10 chars after trim,
  // ≤500 chars). Response 200 carries ONLY the updated request (no timeLog —
  // reject does not touch any TimeLog per Marcus §4.4).
  reject: (id, payload) =>
    apiClient.put(`/workforce/timelog-corrections/${id}/reject`, payload),
};

// Workforce — TimeLog Admin Direct Edit (TIME-010 §4.5)
//
// Distinct from timelogCorrectionAPI because the path is on /timelogs/:id not
// /timelog-corrections/:id, and the audit-trail semantic is one-shot push to
// manualAdjustments[] with type 'admin_edit' (no TimeLogCorrectionRequest
// involved). HIGH-risk action — backend writes riskLevel: 'high' AuditLog.
//
// Body shape per Marcus tech doc §4.5:
//   { punchInTime?, punchOutTime?, jobCode?, breaks?, reason }
// At-least-one-change-supplied OR server returns 400 no_changes_supplied.
// reason is REQUIRED (≥10 chars after trim, ≤500 chars).
//
// Response 200 envelope: { timeLog: {...} }
// The timeLog includes manualAdjustments[] with the new 'admin_edit' entry
// at the LAST index (append-only). The change-history drawer (Zuri §6.5)
// iterates this array in reverse-chronological order.
export const timelogAdminEditAPI = {
  edit: (timeLogId, payload) =>
    apiClient.put(`/workforce/timelogs/${timeLogId}/admin-edit`, payload),
};

// Workforce — StaffProfile lookup (TIME-010 client-side join helper)
//
// Per Marcus tech doc §4.2: GET /timelog-corrections does NOT include
// staffName. Frontend joins requestedByUserId against User OR a separate
// /api/workforce/staff-profiles?active=true call. Per Zuri spec §4.3:
//   1. On tab open, fire GET /timelog-corrections + GET staff-profiles in
//      parallel (Promise.all).
//   2. Build Map<staffProfileId, { name, role }> from the staff response.
//   3. Render at table-time by joining each correction's requestedBy.
//   4. Cache for page lifetime (rare to change mid-session).
//
// This endpoint is the existing staffAPI.list reused here as a SEMANTIC alias
// (TIME-010 spec language) — no new client surface; documented for cross-ref.
// If a slimmer endpoint surfaces in a future Marcus tech doc, swap here.
//
// Note: Marcus's `correctionRequest.requestedBy` is a StaffProfile._id (per
// §3.1 entity field "requestedBy: { type: ObjectId, ref: 'StaffProfile' }").
// The list API at staffAPI.list returns StaffProfile docs with userId joined
// — match correction.requestedBy against profile._id.

// Workforce — Auto-Clock-Out (TIME-007)
//
// Backend contract source-of-truth (Sprint 1 retro action item 6 contract-
// snapshot convention): Marcus's TIME-007 tech doc §5.2 + §5.3 captured-curl
// JSON snapshots. Validated against Simone's PR #71 controller (her PR body
// confirms tests/controllers/autoClockOutController.test.js asserts every
// response key — zero contract drift expected).
//
//   PUT /api/workforce/studio-locations/:id/auto-clock-out-config
//     body { thresholdMinutes (integer, 60-1440), behavior, enabled }
//     → { studioLocation: {...} } (200)
//
//   GET /api/workforce/auto-clock-out/recent?days=7&staffProfileId=&studioLocationId=
//     → { autoClockOutEvents: [...], count, truncated, windowDays, windowFrom } (200)
//
// Note: Zuri's TIME-007 design spec §3.6 + §4.5 documented these as ASSUMED
// shapes. Marcus's tech doc §5.2 + §5.3 RECONCILES them with captured-curl
// JSON. This client cites the TECH-DOC shapes (the canonical ones). Where
// Zuri's spec used `thresholdHours`, Marcus normalized to `thresholdMinutes`
// — the form converts hours↔minutes at the input layer.
//
// `staffName` IS in the GET response (denormalized from User join at the
// controller per Marcus §5.3 note); summary-style read justifies the
// divergence from PAY-005's row-level no-staffName posture. Frontend reads it
// directly, no client-side join needed for this endpoint.
//
// Error-code surface:
//   PUT config: threshold_too_low (minMinutes: 60), threshold_exceeds_hard_cap
//               (maxMinutes: 1440), threshold_not_integer, invalid_behavior,
//               invalid_enabled, studio_location_not_found, admin_required
//   GET config: admin_required (403), studio_location_not_found (404),
//               server_error (500). Bare `null` body on 200 = first-ever-load.
//   GET recent: invalid_days_filter, invalid_staff_profile_id,
//               invalid_studio_location_id, admin_required
export const autoClockOutAPI = {
  // Admin read — get the persisted per-StudioLocation auto-clock-out config.
  // TIME-007-V1.1 contract per Marcus tech doc time-007-v1.1-tech.md §3 + §4.1:
  //   • 200 + sub-doc verbatim ({ thresholdMinutes, behavior, enabled }) when
  //     persisted (Scenario 1)
  //   • 200 + body literal `null` for first-ever-load (parent exists, sub-doc
  //     unset — Scenario 2). FE branches on `response.data === null` (or any
  //     falsy) and falls back to AUTO_CLOCK_OUT_DEFAULTS.
  //   • 403 admin_required for non-admin (Scenario 3 — mirrors PUT RBAC).
  //   • 404 studio_location_not_found when parent StudioLocation does not
  //     exist OR the :id param is a malformed ObjectId (per merged
  //     controller PR #87). NOT used for first-ever-load.
  //   • 500 server_error on downstream failure (Scenario 6 — FE renders
  //     error banner + defaults + retry per Zuri TIME-007-V1.1-design.md §3.4).
  getConfig: (studioLocationId) =>
    apiClient.get(
      `/workforce/studio-locations/${studioLocationId}/auto-clock-out-config`
    ),

  // Admin write — set per-StudioLocation auto-clock-out config.
  // payload: { thresholdMinutes, behavior, enabled }
  setConfig: (studioLocationId, payload) =>
    apiClient.put(
      `/workforce/studio-locations/${studioLocationId}/auto-clock-out-config`,
      payload
    ),

  // Admin read — recent auto-clock-out events for forensic review.
  // params: { days? (default 7, max 30), staffProfileId?, studioLocationId? }
  getRecent: (params) =>
    apiClient.get("/workforce/auto-clock-out/recent", { params }),
};

// Validation API
export const validationAPI = {
  // Trigger manual validation
  runValidation: () => apiClient.post("/staff/validation/run"),

  // Get validation status
  getStatus: () => apiClient.get("/staff/validation/status"),

  // List validation reports
  getReports: (params) =>
    apiClient.get("/staff/validation/reports", { params }),

  // Get specific report
  getReport: (filename, download = false) =>
    apiClient.get(`/staff/validation/reports/${filename}`, {
      params: { download },
    }),

  // Delete report
  deleteReport: (filename) =>
    apiClient.delete(`/staff/validation/reports/${filename}`),

  // Get statistics
  getStatistics: (days = 30) =>
    apiClient.get("/staff/validation/statistics", { params: { days } }),

  // Cleanup old reports (admin only)
  cleanupReports: (retentionDays = 90) =>
    apiClient.post("/staff/validation/cleanup", { retentionDays }),

  // Get dashboard summary
  getDashboard: () => apiClient.get("/staff/validation/dashboard"),
};

// Workforce — Schedule Audit API (SCHED-SLICE1-F)
//
// Recommended BE contract (Kenya proposed in story Notes; awaiting Simone's
// landing on SCHED-SLICE1-A's read-side surface):
//
//   GET /api/workforce/schedule/audit?weekStart=YYYY-MM-DD              — per-week
//   GET /api/workforce/schedule/audit?weekStart=YYYY-MM-DD&shiftId=...  — per-shift
//
// Admin-only RBAC enforced BE-side per tech doc §"Audit-log RBAC".
//
// Expected response shape (per Marcus tech doc §"AuditLog — extension"):
//   {
//     auditRows: [
//       {
//         _id: string,
//         action: 'shift_created' | 'shift_edited' | 'shift_deleted' |
//                 'shift_published' | 'shift_cancelled' | 'schedule_published',
//         category: 'workforce',
//         createdAt: ISOString,
//         createdBy: string,               // User._id (actor)
//         actorName: string,               // pre-resolved by BE (preferred)
//         metadata: {
//           shiftId?: string,
//           staffProfileId?: string,
//           startTime?: ISOString,
//           endTime?: ISOString,
//           roleCode?: string,
//           status?: string,
//           before?: object,               // shift_edited only
//           after?: object,                // shift_edited only
//           reason?: string,               // shift_cancelled only
//           publishedAt?: ISOString,
//           cancelledAt?: ISOString,
//           shiftIds?: string[],           // schedule_published only
//           dateRange?: { from, to },      // schedule_published only
//         },
//       },
//       ...
//     ],
//     // Optional pre-joined lookups for diff rendering (BE may emit or skip):
//     staffNameById: { [staffProfileId]: name },
//     userNameById:  { [userId]: name },
//   }
//
// Loose-coupling posture: until Simone confirms this endpoint, FE renders
// dev fixtures if `localStorage.SCHED_AUDIT_DEV_FIXTURES === 'true'`.
export const scheduleAuditAPI = {
  listAudit: ({ weekStart, shiftId } = {}) => {
    if (
      typeof window !== "undefined" &&
      window.localStorage &&
      window.localStorage.getItem &&
      window.localStorage.getItem("SCHED_AUDIT_DEV_FIXTURES") === "true"
    ) {
      // Inline dev fixture — gated by localStorage flag, no prod impact.
      // eslint-disable-next-line global-require
      const { getScheduleAuditFixture } = require("./scheduleAuditFixtures");
      return Promise.resolve({ data: getScheduleAuditFixture({ weekStart, shiftId }) });
    }
    const params = { weekStart };
    if (shiftId) params.shiftId = shiftId;
    return apiClient.get("/workforce/schedule/audit", { params });
  },
};

// ============================================================================
// SCHED-SLICE1-G — Staff schedule view (read-only)
// ============================================================================
//
// Endpoint:
//   GET /api/workforce/my-shifts?from=ISO&to=ISO
//
// Spec ref:
//   docs/architecture/workforce-scheduling-tech.md §"Staff endpoints (view-as-
//   staff)" + §"GET /api/workforce/my-shifts — staff own-shifts view".
//
// Response shape (locked at contract level by Marcus's tech doc):
//   {
//     shifts: [
//       {
//         _id: string,
//         staffProfileId: string,    // always req.user's own per BE-side RBAC
//         startTime: ISOString,
//         endTime: ISOString,
//         roleCode: string,
//         status: 'published',       // BE redacts non-published per §3.1
//         notes: string | null,
//         weekStartDate: string,     // YYYY-MM-DD
//         updatedAt?: ISOString,     // optional — used for change detection
//       },
//       ...
//     ],
//     cancelledShifts: [             // recently-cancelled shifts in the
//       /* same shape as above */    // visible date range; Slice 1 may
//     ],                             // omit-or-empty
//     // Optional — BE may emit to distinguish 11.2 ("no shifts") from
//     // 11.3 ("no published schedule for week"). Falsy = treat as 11.3.
//     schedulePublishedForWeek?: boolean,
//   }
//
// RBAC: server-side derives staffProfileId from Bearer token; client cannot
// supply a target. Hardcoded enforcement: hourlyRate is NEVER in this
// response per §17.1.1 — if it ever appears, raise as a BE bug to Simone.
//
// Loose-coupling posture: until Simone confirms this endpoint, FE renders
// dev fixtures if `localStorage.SCHED_MY_SCHEDULE_DEV_FIXTURES === 'true'`
// (or one of the variant strings: 'empty', 'no-schedule', 'starts-soon').
export const staffScheduleAPI = {
  listMyShifts: ({ from, to } = {}) => {
    if (
      typeof window !== "undefined" &&
      window.localStorage &&
      window.localStorage.getItem
    ) {
      const flag = window.localStorage.getItem("SCHED_MY_SCHEDULE_DEV_FIXTURES");
      if (flag) {
        // eslint-disable-next-line global-require
        const { getMyScheduleFixture } = require("./staffScheduleFixtures");
        // Re-derive weekStart from `from` for fixture indexing.
        const weekStart = from ? String(from).slice(0, 10) : undefined;
        return Promise.resolve({
          data: getMyScheduleFixture({ weekStart }, flag),
        });
      }
    }
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    return apiClient.get("/workforce/my-shifts", { params });
  },
};

// Workforce — Admin Schedule Builder API (SCHED-SLICE1-E + SCHED-SLICE1-C)
//
// Consumes the SCHED-SLICE1-B + SCHED-SLICE1-C endpoint clusters on main
// (PR #128 + PR #132). All routes admin-RBAC server-side; FE is
// defense-in-depth.
//
//   GET    /api/workforce/shifts?from=ISO&to=ISO[&staffProfileId&status]
//   POST   /api/workforce/shifts                -> 201 { shift, conflicts }
//   PUT    /api/workforce/shifts/:id            -> 200 { shift, conflicts }
//   DELETE /api/workforce/shifts/:id            -> 204
//   GET    /api/workforce/shifts/staff          -> { staff: [...] }
//   POST   /api/workforce/shifts/:id/cancel     -> 200 { shift } (SCHED-1C)
//
//   POST   /api/workforce/schedules/publish     (SCHED-1C)
//     body: { from, to, shiftIds?, confirmConflicts? }
//     200  -> { publishedAt, shiftIds, shifts, conflicts, confirmedConflicts }
//     400  -> { code: 'PUBLISH_CONFIRM_REQUIRED', conflicts: {...} }
//          -> { code: 'NO_SHIFTS_TO_PUBLISH' }
//          -> { code: 'MISSING_DATE_RANGE' | 'INVALID_DATE_FORMAT' | ... }
//
//   Cancel error codes (SCHED-1C):
//     400 'MISSING_REASON'           — reason field absent or empty
//     400 'SHIFT_ALREADY_CANCELLED'  — terminal state, refuse re-cancel
//     400 'SHIFT_NOT_PUBLISHED'     — only published shifts use cancel; drafts use DELETE
//     404 'SHIFT_NOT_FOUND'
export const workforceShiftsAPI = {
  listShifts: ({ from, to, staffProfileId, status } = {}) => {
    const params = { from, to };
    if (staffProfileId) params.staffProfileId = staffProfileId;
    if (status) params.status = status;
    return apiClient.get("/workforce/shifts", { params });
  },
  listAdminStaff: ({ active, employmentType, roleCode } = {}) => {
    const params = {};
    if (active !== undefined) params.active = active;
    if (employmentType) params.employmentType = employmentType;
    if (roleCode) params.roleCode = roleCode;
    return apiClient.get("/workforce/shifts/staff", { params });
  },
  createShift: (body) => apiClient.post("/workforce/shifts", body),
  updateShift: (id, body) => apiClient.put(`/workforce/shifts/${id}`, body),
  deleteShift: (id) => apiClient.delete(`/workforce/shifts/${id}`),
  // SCHED-SLICE1-C cancel-with-reason — published shifts ONLY. Draft
  // shifts continue to use `deleteShift` (DELETE). See AC Scenario 14.
  cancelShift: (id, body) =>
    apiClient.post(`/workforce/shifts/${id}/cancel`, body),
  // SCHED-SLICE1-C bulk publish — direct BE call now that PR #132 is on
  // main. Dev-fixture gate retired; the orphan fixtures module was
  // deleted alongside this edit.
  publishSchedule: (body) =>
    apiClient.post("/workforce/schedules/publish", body),
};

// Workforce — Staff Availability self-service CRUD (SCHED-SLICE1-H consumer
// of SCHED-SLICE1-D BE endpoints, PR #130 merged 2026-05-12).
//
//   GET    /api/workforce/availability        -> { availability: [...] }
//   POST   /api/workforce/availability        -> 201 { availability }
//   PUT    /api/workforce/availability/:id    -> 200 { availability }
//   DELETE /api/workforce/availability/:id    -> 204
//
// The BE derives `staffProfileId` from `req.user._id` -> StaffProfile.userId
// per SCHED-1D Interpretation A. The FE NEVER passes a client-supplied
// `staffProfileId` field. Any such field would be silently ignored by the
// controller (see workforceStaffShiftsController.validateAvailabilityCreate
// / validateAvailabilityEdit — neither reader looks at the field), but
// keeping it off the wire is the cleaner defense-in-depth posture.
//
// Body shape (POST):
//   {
//     dayOfWeek:     0-6,                       // Sun=0 .. Sat=6
//     startTime:     "HH:MM" 24-hour,
//     endTime:       "HH:MM" 24-hour, > startTime,
//     effectiveDate: ISO 8601 (required),
//     endDate:       ISO 8601 | null (optional),
//     isRecurring:   boolean (defaults true server-side),
//     notes:         string (optional, ≤280 chars per AC Scenario 3).
//   }
// Body shape (PUT): partial — any subset of the create fields.
export const workforceAvailabilityAPI = {
  listMine: () => apiClient.get("/workforce/availability"),
  create: (body) => apiClient.post("/workforce/availability", body),
  update: (id, body) => apiClient.put(`/workforce/availability/${id}`, body),
  remove: (id) => apiClient.delete(`/workforce/availability/${id}`),
};

// Workforce — Admin sets staff availability ON BEHALF (SCHED-SLICE1-K).
//
// Endpoint family (admin-namespaced, Marcus RBAC LOCK Option A — see
// `docs/architecture/SCHED-SLICE1-K-rbac-lock.md`):
//
//   GET    /api/workforce/admin/staff/:userId/availability
//                                            -> { availability: [...] }
//   POST   /api/workforce/admin/staff/:userId/availability
//                                            -> 201 { availability }
//   PUT    /api/workforce/admin/staff/:userId/availability/:availabilityId
//                                            -> 200 { availability }
//   DELETE /api/workforce/admin/staff/:userId/availability/:availabilityId
//                                            -> 204
//
// `:userId` is the staff member's `User._id` (NOT `StaffProfile._id`); the
// BE resolves StaffProfile server-side via `StaffProfile.findOne({ userId })`
// and rejects with 404 if no profile exists. The FE NEVER passes a body
// `staffProfileId` — defense-in-depth invariant preserved across both the
// staff-self-serve and admin-on-behalf namespaces.
//
// Body shape (POST) — mirror of staff-self-serve, plus admin context only
// in the AuditLog (no surface field changes from caller's perspective):
//   {
//     dayOfWeek:     0-6,
//     startTime:     "HH:MM" 24-hour,
//     endTime:       "HH:MM" 24-hour, > startTime,
//     isRecurring:   boolean,
//     effectiveDate: ISO 8601,
//     endDate:       ISO 8601 | null,
//     notes:         string (optional).
//   }
//
// Response records carry a `setByAdmin: boolean` field derived per-record
// by the BE from the most-recent `AuditLog` row's `metadata.setByAdmin`
// (Marcus RBAC LOCK §"GET ..."). FE renders the "Set by admin" badge from
// this field; no client-side derivation.
export const workforceAdminAvailabilityAPI = {
  list: (userId) =>
    apiClient.get(`/workforce/admin/staff/${userId}/availability`),
  create: (userId, body) =>
    apiClient.post(`/workforce/admin/staff/${userId}/availability`, body),
  update: (userId, availabilityId, body) =>
    apiClient.put(
      `/workforce/admin/staff/${userId}/availability/${availabilityId}`,
      body
    ),
  remove: (userId, availabilityId) =>
    apiClient.delete(
      `/workforce/admin/staff/${userId}/availability/${availabilityId}`
    ),
};

// Workforce — Admin "All Availability" aggregate dashboard read endpoint
// (SCHED-AVAIL-DASHBOARD-001-A BE, PR #149 merged 2026-05-12; extended by
// SCHED-AVAIL-DASHBOARD-002-A BE, PR #153 merged 2026-05-13 to honor
// `includeOneOff=true` and accept `weekStart=YYYY-MM-DD`).
//
//   GET /api/workforce/admin/availability
//     ?active=true|false     (default true)
//     &primaryRole=<string>  (optional)
//     &includeOneOff=true    (Slice 2 — when true, one-off rows folded in)
//     &weekStart=YYYY-MM-DD  (Slice 2 — Phoenix-MST Sunday; default current week)
//
// Response shape (per docs/architecture/workforce-admin-availability-dashboard-tech.md
// §"Data Models" + AC Scenario 1):
//
//   {
//     generatedAt: ISO 8601 string,
//     staff: [
//       {
//         staffProfileId: string,
//         userId:         string,          // pass THIS (NOT staffProfileId)
//         displayName:    string,          // server-built "First Last"; falls back to
//                                          // email -> "Unknown staff"
//         email:          string,
//         primaryRole:    string,          // e.g. "massage_therapist", "front_desk"
//         active:         boolean,
//         availability: {
//           "0": [AdminAvailabilityWindow], // Sun
//           "1": [...],                     // Mon
//           ...                             // every day 0..6 always present
//           "6": [...]                      // Sat
//         }
//       },
//       ...
//     ]
//   }
//
// AdminAvailabilityWindow:
//   { _id, startTime: "HH:MM", endTime: "HH:MM", isRecurring: boolean,
//     effectiveDate: ISO, endDate: ISO|null, notes?: string }
//
// Defense-in-depth invariants honored by the BE controller (informational —
// the FE never references these fields, so future drift cannot mis-render
// payroll PII into the matrix):
//   - hourlyRate / hourlyRateEffectiveDate / payrollVendorRef NEVER returned
//   - createdBy / lastModifiedBy on StaffProfile NEVER returned
//
// The drill-down write path stays on the existing SCHED-1K endpoint family
// (workforceAdminAvailabilityAPI above) keyed on `:userId` from a row, NOT
// staffProfileId — see SCHED-1K rbac-lock §"Defense-in-depth invariants".
//
// Filter param normalization:
//   - active: boolean | undefined — only forwarded when explicitly true/false
//   - primaryRole: trimmed non-empty string only
//   - includeOneOff: boolean | undefined — only forwarded when true
//   - weekStart: "YYYY-MM-DD" string only — forwarded verbatim when it matches
//     the strict shape; malformed values are dropped (BE then falls back to
//     "current Phoenix-MST week Sunday" silently per Marcus §"Read-path
//     defaulting"). FE-side drop is purely a UX hygiene step — the BE never
//     400s on a bad weekStart.
// Empty / falsy filters are dropped from the query string so the BE applies
// its defaults (active=true, no role filter, includeOneOff=false).
const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

export const workforceAdminAvailabilityDashboardAPI = {
  list: (filters = {}) => {
    const params = {};
    if (typeof filters.active === "boolean") {
      params.active = filters.active ? "true" : "false";
    }
    if (
      typeof filters.primaryRole === "string" &&
      filters.primaryRole.trim()
    ) {
      params.primaryRole = filters.primaryRole.trim();
    }
    if (filters.includeOneOff === true) {
      params.includeOneOff = "true";
    }
    if (
      typeof filters.weekStart === "string" &&
      YYYY_MM_DD.test(filters.weekStart.trim())
    ) {
      params.weekStart = filters.weekStart.trim();
    }
    return apiClient.get("/workforce/admin/availability", { params });
  },
};

// Workforce — Admin bulk one-off availability create endpoint
// (SCHED-AVAIL-DASHBOARD-003-A BE, PR #168 merged 2026-05-14).
//
//   POST /api/workforce/admin/availability/bulk
//
// Spec reconciliation note (refer to:
//   /mnt/c/development/studio-web-app/backend/controllers/
//   workforceAdminAvailabilityBulkController.js header comment):
//
// Marcus's tech doc (docs/architecture/workforce-admin-availability-dashboard
// -slice-3-tech.md) locks the wire shape as SINGLE-STAFF × N-WINDOWS — one
// `userId` per request with 1..20 one-off windows. Amara's story body and
// Zuri's visual spec described a multi-staff × 1-window shape; the BE
// controller comment explicitly resolves the divergence in favor of Marcus's
// shape ("Imani's dispatch brief explicit 'Implement per Marcus tech doc.'").
// The FE consumer therefore frames the bulk workflow as "one staff, 1..20
// one-off windows" (e.g., "Jamie can cover Tue/Thu/Sat 9-2 next week" → 3
// rows for Jamie). A future multi-staff bulk story (Slice 4+) can layer N
// parallel single-staff bulk calls without a contract break.
//
// Body shape (POST):
//   {
//     userId:  string,                 // target staff User._id; BE resolves
//                                       // to StaffProfile and validates role
//     windows: BulkOneOffWindow[],     // 1..20 rows
//     notes:   string (optional)       // applied to rows without their own
//   }
//
// BulkOneOffWindow:
//   {
//     dayOfWeek:     0-6,              // must match effectiveDate in MST
//     startTime:     "HH:MM",
//     endTime:       "HH:MM",          // > startTime
//     effectiveDate: ISO 8601,         // calendar day in America/Phoenix
//     endDate:       ISO 8601 | null,  // optional; BE forces = effectiveDate
//     notes:         string (optional) // row-level override of caller notes
//                                       // isRecurring is server-forced false
//   }
//
// Response 201:
//   {
//     bulkOperationId: string (UUIDv4),
//     created:         number,
//     rows:            AdminAvailabilityWindow[],  // serialized; setByAdmin:true
//     conflicts:       ConflictDescriptor[]        // empty in v1; populated by
//                                                   // SLICE3-B (future story)
//   }
//
// Response 4xx error codes (FE-relevant):
//   - 400 INVALID_BODY / INVALID_USER_ID / BULK_EMPTY_BATCH / INVALID_NOTES
//   - 400 BULK_BATCH_TOO_LARGE         — message includes the cap (20)
//   - 400 BULK_VALIDATION_FAILED       — errors: { [rowIndex]: { code, message } }
//                                         per-row codes: BULK_RECURRING_NOT_ALLOWED,
//                                         INVALID_DAY_OF_WEEK, INVALID_START_TIME,
//                                         INVALID_END_TIME, INVALID_TIME_RANGE,
//                                         MISSING_EFFECTIVE_DATE, INVALID_EFFECTIVE_DATE,
//                                         INVALID_END_DATE, INVALID_NOTES,
//                                         ONE_OFF_END_DATE_MISMATCH,
//                                         ONE_OFF_DAY_OF_WEEK_MISMATCH
//   - 403 NOT_ADMIN (middleware)
//   - 404 STAFF_NOT_FOUND               — user missing OR no StaffProfile
//   - 500 INTERNAL_ERROR
//
// Production-safety: pure-additive call site. Existing
// `workforceAdminAvailabilityAPI` (single-row CRUD) UNCHANGED. The bulk
// helper is consumed only by the new AdminBulkOneOffAvailabilityModal —
// single-revert posture if the bulk feature must be rolled back.
export const workforceAdminAvailabilityBulkAPI = {
  create: (body) =>
    apiClient.post("/workforce/admin/availability/bulk", body),
};

// MAILCHIMP-COMMS-001 — Operational Broadcast (Slice 1A).
// BE: PR #156 (merged). Endpoints under /api/comms/broadcast.
//
//   send(payload)        -> POST   /comms/broadcast  (returns 202 + broadcastLogId)
//   list(filters)        -> GET    /comms/broadcast  (paginated history)
//   getById(id)          -> GET    /comms/broadcast/:id
//
// Send payload shape (BE-locked per docs/architecture/mailchimp-comms-001-A-tech.md
// + commsController.js):
//   {
//     composeMode: "template" | "freeform",
//     // template mode:
//     templateId?: string,                      // ObjectId
//     templateVariables?: Record<string, string>,
//     // freeform mode:
//     subject?: string,                         // required when freeform; <=200 chars
//     bodyHtml?: string,                        // required when freeform; <=100KB (UTF-8 bytes)
//     bodyText?: string,                        // optional plaintext alt
//     // both modes:
//     recipientUserIds?: string[],              // User._id values
//     recipientEmails?: string[],               // raw emails (server normalizes + dedupes)
//   }
//
// Send response 202:
//   { broadcastLogId, status, recipientCount, message }
//
// Recipient cap (RBAC-conditional): staff 50 / admin 500. 400 RECIPIENT_CAP_EXCEEDED
// carries { cap, role, attempted }. BODY_SANITIZED_TO_EMPTY surfaces when DOMPurify
// strips a freeform body to empty (e.g., pure <script>).
//
// list() supports BE-honored filters: senderUserId, from, to, limit, cursor.
// Staff queries are server-scoped to senderUserId=self regardless of input.
export const broadcastAPI = {
  send: (payload) => apiClient.post(ENDPOINTS.COMMS.BROADCAST, payload),

  list: (filters = {}) => {
    const params = {};
    if (
      typeof filters.senderUserId === "string" &&
      filters.senderUserId.trim()
    ) {
      params.senderUserId = filters.senderUserId.trim();
    }
    if (typeof filters.from === "string" && filters.from.trim()) {
      params.from = filters.from.trim();
    }
    if (typeof filters.to === "string" && filters.to.trim()) {
      params.to = filters.to.trim();
    }
    if (typeof filters.limit === "number" && filters.limit > 0) {
      params.limit = Math.min(filters.limit, 100);
    }
    if (typeof filters.cursor === "string" && filters.cursor.trim()) {
      params.cursor = filters.cursor.trim();
    }
    return apiClient.get(ENDPOINTS.COMMS.BROADCAST, { params });
  },

  getById: (id) => apiClient.get(`${ENDPOINTS.COMMS.BROADCAST}/${id}`),
};

// PROMO-RF-E — Stripe staff-console promo-code management (ADR-029 D2/D5).
// Consumes the PROMO-RF-B pass-through endpoints (PR #181, merged) VERBATIM:
//   list()     -> GET  /api/staff/promo-codes        (?active=, ?limit=)
//   getById()  -> GET  /api/staff/promo-codes/:id
// Response envelope: { success: true, data: <shaped|[shaped]>, hasMore? }.
// Shaped row/detail (RF-B `shapePromotionCode`): { id, code, active,
//   status: "active"|"archived", timesRedeemed, maxRedemptions, expiresAt,
//   created, restrictions, metadata, coupon: { id, name, percentOff,
//   amountOff, currency, duration, valid } | null }.
// Error envelope (ADR-029 D3): { success:false, error, reason }; HTTP 503 =
// vendor_unavailable (retryable). Stripe is the source of truth (Q1=A) — the
// in-house PromoCode collection is NOT read (B1: no in-house redemption
// history). ADR-029 D2: staff management metadata ONLY — no in-app customer
// discount math.
//
// PROMO-RF-F (←RF-B / PR #181) EXTENDS this with create()/archive(),
// consumed VERBATIM from the deployed RF-B controller/service contract:
//   create()  -> POST /api/staff/promo-codes
//                body { code, discountType:"percentage"|"amount",
//                       discountValue (percent 1..100 OR amount in cents),
//                       name?, expiresAt? (unix s OR ISO/date string),
//                       maxRedemptions?, minPurchaseAmountCents?, metadata? }
//                -> 201 { success:true, data:<shaped> }
//   archive() -> POST /api/staff/promo-codes/:id/archive  (no body — BE
//                derives archivedBy from req.user; Stripe active:false,
//                NOT a destructive delete) -> 200 { success:true,
//                data:<shaped active:false> }
// Error envelope is identical to list/getById (ADR-029 D3). A duplicate
// code at Stripe surfaces as reason "invalid_request" (HTTP 400) with a
// BE-provided non-raw `error` string; an upstream Stripe outage surfaces
// as reason "vendor_unavailable" (HTTP 503, retryable). NO in-house
// authoritative write (Q1=A); NO legacy redemption-history read (B1).
export const staffPromoAPI = {
  list: (filters = {}) => {
    const params = {};
    if (filters.active === true) {
      params.active = "true";
    } else if (filters.active === false) {
      params.active = "false";
    }
    if (typeof filters.limit === "number" && filters.limit > 0) {
      params.limit = Math.min(filters.limit, 100);
    }
    return apiClient.get(ENDPOINTS.STAFF_PROMO.LIST, { params });
  },

  getById: (promotionCodeId) =>
    apiClient.get(
      `${ENDPOINTS.STAFF_PROMO.LIST}/${encodeURIComponent(promotionCodeId)}`
    ),

  // PROMO-RF-F — create a Stripe promo code. `payload` is forwarded
  // VERBATIM to the RF-B create endpoint; the FE component is responsible
  // for client-side validation (AC Scenario 2) before this is called.
  create: (payload = {}) =>
    apiClient.post(ENDPOINTS.STAFF_PROMO.LIST, payload),

  // PROMO-RF-F — archive (Stripe active:false deactivate, NOT delete).
  // Reversible-by-recreate only. No request body — the BE derives the
  // audit `archivedBy` from the authenticated staff user.
  archive: (promotionCodeId) =>
    apiClient.post(
      `${ENDPOINTS.STAFF_PROMO.LIST}/${encodeURIComponent(
        promotionCodeId
      )}/archive`
    ),
};

// Monthly sales report (Exhibit G for the landlord) and the GM check.
// Backend: backend/routes/salesReportRoutes.js (#290). Admins and holders of
// the "sales_report" permission only. Read-only from Stripe; the only write
// is the statement text (report settings), which is audited.
const salesReportParams = (years) => ({
  params: { years: (Array.isArray(years) ? years : [years]).join(",") },
});
export const salesReportAPI = {
  getReport: (years) => apiClient.get("/admin/reports/sales", salesReportParams(years)),
  downloadExhibitG: (years) =>
    apiClient.get("/admin/reports/sales/exhibit-g.pdf", {
      ...salesReportParams(years),
      responseType: "blob",
    }),
  downloadCheckCsv: (years) =>
    apiClient.get("/admin/reports/sales/check.csv", {
      ...salesReportParams(years),
      responseType: "blob",
    }),
  getSettings: () => apiClient.get("/admin/report-settings"),
  updateSettings: (changes) => apiClient.put("/admin/report-settings", changes),
};

export default apiClient;
